import { useNavigate } from '@tanstack/react-router'
import * as React from 'react'
import { AuthProvider } from 'react-oidc-context'

import { getQueryClient } from 'Common/base/queryClient'
import { withRouterNavigate } from 'Common/components/router/withRouterNavigate'
import { loadEnvironment } from 'Common/environment'
import { Environment } from 'Common/environment/types'
import queryKeys from 'Common/queryKeys/queryKeys'

import SidAuthBridge from './SidAuthBridge'
import { getUserData } from './authHelper'
import { getPublishingHouses } from './getPublishingHouses'
import { AuthMode } from './identityConfig'
import { AuthData, Authentication, initialAuth } from './types'
import { createAuthClient } from './userManagerFactory'

type PropsFromRouter = {
    navigate: ReturnType<typeof useNavigate>
    location: any
    params: any
}

type State = { isAuthenticated: boolean; ready: boolean }

const cleanCallbackUrl = () =>
    window.history.replaceState({}, document.title, window.location.pathname)

const matchSignoutCallback = (settings: { post_logout_redirect_uri?: string }): boolean => {
    if (typeof window === 'undefined' || !settings.post_logout_redirect_uri) return false
    const params = new URLSearchParams(window.location.search)
    if (!params.has('state') || params.has('code') || params.has('error')) return false
    try {
        return window.location.origin === new URL(settings.post_logout_redirect_uri).origin
    } catch {
        return false
    }
}

export function requireAuthentication(Component: React.ComponentType<any>) {
    class AuthenticatedComponent extends React.Component<PropsFromRouter, State> {
        userManager: any

        sidUserManager: any

        mode: AuthMode = 'legacy'

        environment: Environment

        state: State = { isAuthenticated: false, ready: false }

        setAuth = (data: AuthData) => {
            getQueryClient().setQueryData<Authentication>([queryKeys.auth], {
                ...initialAuth,
                isAuthenticated: true,
                data,
            })
            this.setState({ isAuthenticated: true })
        }

        clearAuth = () => {
            getQueryClient().setQueryData<Authentication>([queryKeys.auth], initialAuth)
            this.setState({ isAuthenticated: false })
        }

        componentDidMount() {
            loadEnvironment().then((environment) => {
                this.environment = environment
                const { mode, userManager } = createAuthClient(environment)
                this.mode = mode

                if (mode === 'sid') {
                    this.sidUserManager = userManager
                    this.setState({ ready: true })
                    return
                }

                this.userManager = userManager

                userManager.events.addSilentRenewError(() => {
                    this.signinRedirect()
                })
                userManager.events.addAccessTokenExpired(() => {
                    console.info('token expired')
                    this.signinRedirect()
                })
                userManager.events.addUserSignedOut(() => {
                    console.info('user signed out in another place') //Commenting out this as this seems to throw one user in a loop if using chrome.
                    // this.signinRedirect()
                })
                userManager.events.addUserLoaded((user) => {
                    getPublishingHouses(environment, user.access_token).then((result) => {
                        this.setAuth(getUserData(user, result?.publishingHouses))
                    })
                })

                window.onmessage = (e) => {
                    if (
                        e.origin === `${window.location.protocol}//${window.location.host}` &&
                        e.data === 'changed'
                    ) {
                        this.userManager.removeUser()
                        this.userManager
                            .signinSilent()
                            .then(() => {
                                // Session state changed but we managed to silently get a new identity token, everything's fine
                                console.info('renewTokenSilentAsync success')
                            })
                            .catch((err: Error) => {
                                // Here we couldn't get a new identity token, we have to ask the user to log in again
                                console.warn('renewTokenSilentAsync failed', err.message)
                                this.signinRedirect()
                            })
                    }
                }

                if (window.location.hash.includes('id_token=')) {
                    // validate the callback from the server and give the user the access if valid
                    this.validateUser()
                } else if (!this.state.isAuthenticated) {
                    // redirect user to login page
                    this.signinSilent()
                }
            })
        }

        logout = () => {
            this.userManager.signoutRedirect()
        }

        signinRedirect = () => {
            this.userManager.signinRedirect({
                data: `${window.location.pathname}${window.location.search || ''}`,
            })
        }

        signinSilent = () => {
            this.userManager.signinSilent().catch((err: Error) => {
                console.warn('Error on signin silent', err.message)
                this.userManager.signinRedirect({
                    data: `${window.location.pathname}${window.location.search || ''}`,
                    redirect_uri: `${window.location.origin}`,
                })
            })
        }

        validateUser = () => {
            const callbackUrl = window.location.href
            cleanCallbackUrl()

            this.userManager.signinRedirectCallback(callbackUrl).then(
                (user: { access_token: string; state?: string }) => {
                    getPublishingHouses(this.environment, user.access_token).then((result) => {
                        this.setAuth(getUserData(user, result?.publishingHouses))

                        if (user.state) {
                            this.props.navigate({ href: user.state })
                        } else {
                            this.props.navigate({ to: '/' })
                        }
                    })
                },
                (error: Error) => {
                    console.warn('Problem Getting Token', error.message || error)
                    this.clearAuth()
                    this.props.navigate({ to: '/' })
                },
            )
        }

        render() {
            const { isAuthenticated, ready } = this.state

            if (this.mode === 'sid') {
                if (!ready || !this.sidUserManager) {
                    return <div />
                }
                return (
                    <AuthProvider
                        userManager={this.sidUserManager}
                        onSigninCallback={cleanCallbackUrl}
                        matchSignoutCallback={matchSignoutCallback}
                        onSignoutCallback={cleanCallbackUrl}
                    >
                        <SidAuthBridge environment={this.environment}>
                            {(logout) => <Component {...this.props} logout={logout} />}
                        </SidAuthBridge>
                    </AuthProvider>
                )
            }

            if (isAuthenticated) {
                return <Component {...this.props} logout={this.logout} />
            }
            return <div />
        }
    }

    return withRouterNavigate(AuthenticatedComponent)
}
